import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { useMemberSections } from "@/components/dashboard/memberNav";
import { DashboardOverviewTab } from "@/components/dashboard/DashboardOverviewTab";
import { DashboardTasksTab } from "@/components/dashboard/DashboardTasksTab";
import { DashboardCalendarTab } from "@/components/dashboard/DashboardCalendarTab";
import { DashboardNotificationsTab } from "@/components/dashboard/DashboardNotificationsTab";
import { DashboardProfileTab } from "@/components/dashboard/DashboardProfileTab";
import { DashboardBookingsTab } from "@/components/dashboard/DashboardBookingsTab";
import { DashboardAvailabilityTab } from "@/components/dashboard/DashboardAvailabilityTab";
import { DashboardTicketsTab } from "@/components/dashboard/DashboardTicketsTab";
import { PendingApprovalScreen } from "@/components/dashboard/PendingApprovalScreen";
import client from "@/api/client";
import { toast } from "sonner";
export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const isPending = !!(user && user.status && user.status !== "approved");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await client.get("/dashboard/me");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || t("common.error"));
      }
    }
  }, [t]);
  useEffect(() => { if (!isPending) load(); }, [load, isPending]);

  const select = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const sections = useMemberSections({
    role: user?.role,
    openTasks: data?.stats?.open_tasks || 0,
    unreadCount: unreadCount || 0,
  });

  // Unapproved accounts never reach the workspace — profile + journey only.
  if (isPending) return <PendingApprovalScreen />;

  const PANELS = {
    overview: <DashboardOverviewTab data={data} onSelect={select} />,
    tasks: <DashboardTasksTab data={data} reload={load} />,
    calendar: <DashboardCalendarTab />,
    notifications: <DashboardNotificationsTab />,
    bookings: <DashboardBookingsTab />,
    profile: <DashboardProfileTab />,
    tickets: <DashboardTicketsTab />,
    availability: <DashboardAvailabilityTab />,
  };

  return (
    <div className="overflow-x-clip" data-testid="dashboard-page">
      <DashboardShell sections={sections} active={tab} onSelect={select}>
        {PANELS[tab] || PANELS.overview}
      </DashboardShell>
    </div>
  );
}
